import React from 'react';
import {
  Box, Button, Chip, IconButton, Stack, Tooltip, Typography
} from '@mui/material';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';

const DOC_TYPE_LABELS = {
  ORDER_BOOK: '수주품의서',
};

const AUTH_SCOPE_LABELS = {
  FINAL: '전결 승인자',
  NOTIFIER: '보고 수신자',
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return Number(value).toLocaleString();
};

const formatAmountRange = (row) => {
  if (row.authScope === 'NOTIFIER') {
    return '-';
  }

  const min = formatAmount(row.minAmount);
  const max = formatAmount(row.maxAmount);

  if (!min && !max) {
    return '전체';
  }
  if (!min) {
    return `${max} 이하`;
  }
  if (!max) {
    return `${min} 이상`;
  }
  return `${min} ~ ${max}`;
};

const ApprovalFinalAuthTab = ({
  list,
  loading,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const columns = [
    {
      field: 'docType',
      headerName: '대상 문서',
      minWidth: 130,
      flex: 0.7,
      renderCell: ({ value }) => DOC_TYPE_LABELS[value] || value || '-',
    },
    {
      field: 'authScope',
      headerName: '권한 구분',
      minWidth: 130,
      flex: 0.7,
      renderCell: ({ value }) => AUTH_SCOPE_LABELS[value] || value || '-',
    },
    {
      field: 'usrNm',
      headerName: '권한자',
      minWidth: 140,
      flex: 0.8,
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'dptNm',
      headerName: '부서',
      minWidth: 140,
      flex: 0.8,
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'gradeNm',
      headerName: '직급',
      minWidth: 100,
      flex: 0.5,
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'amountRange',
      headerName: '전결 금액',
      minWidth: 180,
      flex: 1,
      renderCell: ({ row }) => formatAmountRange(row),
    },
    {
      field: 'activeYn',
      headerName: '상태',
      minWidth: 100,
      flex: 0.5,
      renderCell: ({ value }) => (
        <Chip
          label={value === 'Y' ? '사용' : '중지'}
          color={value === 'Y' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'crtDt',
      headerName: '등록일',
      minWidth: 160,
      flex: 0.8,
      valueFormatter: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="수정">
            <IconButton size="small" color="primary" onClick={() => onEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="삭제">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(row)}
                disabled={row.activeYn !== 'Y'}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="body2" fontWeight={600}>
            전결 권한
          </Typography>
          <Typography variant="caption" color="text.secondary">
            수주품의서 전결 승인자와 전결 보고 수신자를 지정합니다.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onAdd}>
          권한 등록
        </Button>
      </Stack>

      <DataGrid
        rows={list}
        columns={columns}
        getRowId={(row) => row.authId}
        loading={loading}
        autoHeight
        density="compact"
        disableRowSelectionOnClick
        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
      />
    </Box>
  );
};

export default ApprovalFinalAuthTab;
